import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { actionBtn, ProgressHeader, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow, isDemoAuthEnabled } from '../../auth/patientAuthFlow';

const RESEND_COOLDOWN_SECONDS = 30;

export default function PatRegOTPScreen() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const [otpRequestId, setOtpRequestId] = useState<string | undefined>();
  const [devCode, setDevCode] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const phone = patientAuthFlow.getState().phone;
  const email = patientAuthFlow.getState().email;
  /**
   * Which contact the code was sent to. SMS is the default, but a text that
   * never arrives (a gateway outage, a number the operator blocks) would
   * otherwise end the whole signup — so a patient who gave an email can have
   * the same code sent there and carry on.
   */
  const [channel, setChannel] = useState<'phone' | 'email'>('phone');

  const inFlight = useRef(false);
  const sendOtp = async (via: 'phone' | 'email' = channel) => {
    // A second request while one is in flight costs another SMS and, worse,
    // replaces the code the patient is about to type.
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setDevCode(undefined);
    const identifier = via === 'email' ? email : phone;
    if (!identifier) {
      setError(via === 'email' ? 'No email address on this registration.' : 'No phone number on this registration.');
      inFlight.current = false;
      return;
    }
    setChannel(via);
    const result = await patientAuthFlow.backendRequestOtp(via, identifier);
    if (result.success) {
      setOtpRequestId(result.otpRequestId);
      setOtp(['', '', '', '', '', '']);
      setCountdown(RESEND_COOLDOWN_SECONDS);
      // Only ever populated when VITE_ENABLE_DEMO_AUTH=true (see isDemoAuthEnabled) —
      // shown so local testing doesn't require digging through backend logs.
      setDevCode(result.demoOtp);
    } else {
      // A send that failed is ALWAYS said out loud, demo auth or not. Hiding
      // it left the screen looking like a code was on its way — the patient
      // waits for a text that was never accepted, and nothing explains why.
      // Demo auth still lets the verify step through; it does not get to
      // pretend the send worked.
      setError(result.error
        ? `Could not send the code: ${result.error}`
        : 'Could not send verification code — please check your connection and try again.');
    }
    inFlight.current = false;
  };

  /**
   * Send the first code exactly once.
   *
   * Without the guard this effect fires twice under StrictMode, and each run
   * is a separate `send-otp`: the patient gets two texts a second apart with
   * two different codes, the screen keeps only the second request, and the
   * code their phone autofills (the first one) comes back refused — then
   * "this code has expired" on the retry, because it was spent. Two SMS
   * charged per signup, for a code that could not work.
   */
  const firstSendStarted = useRef(false);
  useEffect(() => {
    if (firstSendStarted.current) return;
    firstSendStarted.current = true;
    sendOtp('phone');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerify = async () => {
    setError('');
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }

    if (otpRequestId) {
      setBusy(true);
      const result = await patientAuthFlow.backendVerifyOtp({ otpRequestId, code });
      setBusy(false);
      if (result.success) {
        patientAuthFlow.verifyOTP();
        navigate('/patient/register/health');
        return;
      }
      setError(result.error ?? 'Invalid code — please try again.');
      return;
    }

    // No otpRequestId means the initial send failed (backend unreachable).
    if (!isDemoAuthEnabled()) {
      setError('Could not verify — please check your connection and try again.');
      return;
    }
    patientAuthFlow.verifyOTP();
    navigate('/patient/register/health');
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <WebEntryLayout>
    <div style={{ flex: 1, background: T.white, display: 'flex', flexDirection: 'column', fontFamily: T.font }}>
      <ProgressHeader title="OTP Verification" step={3} total={8} onBack={() => navigate(-1)} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 24px' }}>
        {/* Phone Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            background: `${T.teal}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72" />
            <path d="M15.05 5A5 5 0 0119 8.95M15.05 1A9 9 0 0123 8.94" />
          </svg>
        </div>

        {/* Title */}
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 6 }}>
          Verify your phone number
        </div>

        {/* Subtitle */}
        <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginBottom: devCode ? 8 : 28 }}>
          {channel === 'email'
            ? (email ? `Code sent to ${email}` : 'Enter the code sent to your email')
            : (phone ? `OTP sent to +91 ${phone}` : 'Enter the code sent to your phone')}
        </div>

        {devCode && (
          <div style={{
            fontSize: 10, color: T.warning, fontFamily: T.mono, fontWeight: 700,
            marginBottom: 20, padding: '4px 10px', background: `${T.warning}14`,
            borderRadius: 8, border: `1px solid ${T.warning}40`,
          }}>
            DEV MODE — code is {devCode}
          </div>
        )}

        {/* OTP Boxes */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, width: '100%', maxWidth: 320, justifyContent: 'center' }}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
              }}
              style={{
                width: 44,
                height: 52,
                borderRadius: 10,
                border: digit ? `1.5px solid ${T.teal}` : `1.5px solid ${T.border}`,
                background: digit ? `${T.teal}0A` : T.white,
                textAlign: 'center',
                fontSize: 20,
                fontWeight: 700,
                fontFamily: T.mono,
                color: T.text,
                outline: 'none',
                boxSizing: 'border-box',
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{ fontSize: 10, color: T.danger, marginBottom: 16, textAlign: 'center' }}>{error}</div>
        )}

        {/* Verify Button */}
        <button
          disabled={busy}
          onClick={handleVerify}
          style={{
            ...actionBtn('Verify', T.teal, T.white),
            width: '100%',
            padding: 14,
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
          }}
        >
          {busy ? 'Verifying…' : 'Verify'}
        </button>

        {/* Resend */}
        <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 16 }}>
          {countdown > 0 ? `Resend OTP in ${minutes}:${seconds.toString().padStart(2, '0')}` : (
            <span style={{ color: T.teal, cursor: 'pointer', fontWeight: 600 }} onClick={() => sendOtp()}>
              Resend OTP
            </span>
          )}
        </div>

        {/* A text that never arrives must not end the signup. */}
        {email && (
          <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 10, textAlign: 'center', lineHeight: 1.6 }}>
            {channel === 'phone' ? "Didn't get the text? " : 'Prefer a text? '}
            <span
              style={{ color: T.teal, cursor: 'pointer', fontWeight: 700 }}
              onClick={() => sendOtp(channel === 'phone' ? 'email' : 'phone')}
            >
              {channel === 'phone' ? `Send the code to ${email} instead` : 'Send it to my phone instead'}
            </span>
          </div>
        )}
      </div>
    </div>
    </WebEntryLayout>
  );
}
